import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import Layout from '../Components/Layout/Layout';
import ProductList from '../Components/Products/ProductList';
import ProductTabs from '../Components/Products/ProductTabs';
import SimpleQRDialog from '../Components/Products/SimpleQRDialog';
import { Button } from "@/Components/ui/button";
import { Plus, Scan } from "lucide-react";
import { useNavigate } from 'react-router-dom';
import axiosInstance from '../utils/axiosConfig';
import * as signalR from '@microsoft/signalr';

const Products = () => {
    const { t } = useTranslation();
    const [products, setProducts] = useState([]);
    const [activeTab, setActiveTab] = useState('all');
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState(null);
    const [isQROpen, setIsQROpen] = useState(false);
    const navigate = useNavigate();
    const [connection, setConnection] = useState(null);
    const [connectionStatus, setConnectionStatus] = useState('disconnected');

    useEffect(() => { 
        const signalRConnection = new signalR.HubConnectionBuilder()
            .withUrl('https://localhost:5001/producthub', {
                accessTokenFactory: () => {
                    const token = localStorage.getItem('token');
                    return token.replace('Bearer ', '');
                }
            })
            .withAutomaticReconnect()
            .build();

        setConnection(signalRConnection);

        signalRConnection.start()
            .then(() => {
                setConnectionStatus('connected');
                console.log('SignalR Connected');
            })
            .catch(err => console.error('SignalR Connection Error:', err));
             
        signalRConnection.on('ReceiveProductScan', (product) => {
            setProducts(prev => [...prev, product]);
            window.scrollTo({ top: document.documentElement.scrollHeight, behavior: 'smooth' });
        });

        return () => {
            if (signalRConnection) {
                signalRConnection.stop();
            }
        };
    }, []);

    useEffect(() => {
        const fetchProducts = async () => {
            try {
                setIsLoading(true);
                const response = await axiosInstance.get(`/product/GetProducts/${activeTab}`);
                const data = await response.data;
                if (Array.isArray(data)) {
                    setProducts(data);
                } else if (data.products && Array.isArray(data.products)) {
                    setProducts(data.products);
                } else {
                    console.error('Unexpected API response format:', data);
                    setProducts([]);
                }
            } catch (error) {
                console.error('Error fetching products:', error);
                setError(error.message);
            } finally {
                setIsLoading(false);
            }
        };

        fetchProducts();
    }, [activeTab]);

    const handleProductClick = (productId) => {
        navigate('/product', {
            state: {
                productId: productId,
                returnPage: 'products',
                activeTab: activeTab
            }
        });
    };

    const handleAddProduct = () => {
        navigate('/productsearch', {
            state: { 
                returnPage: 'products',
                activeTab: activeTab
            }
        });
    }; 

    const handleScanProduct = () => {
        setIsQROpen(true);
    };

    if (isLoading) return (
        <Layout>
            <div>{t('common.loading')}</div>
        </Layout>
    );
    
    if (error) return (
        <Layout>
            <div>{t('common.error')}: {error}</div>
        </Layout>
    );

    return (
        <Layout>
            <div className="relative"> 
                <SimpleQRDialog
                    isOpen={isQROpen}
                    onClose={() => setIsQROpen(false)}
                />
            </div>

            <div className="relative">
                <h2 className="text-xl font-normal font-inter text-ps-blue-dark mb-8">
                    {t('products.title')}
                </h2>
                <ProductTabs activeTab={activeTab} onTabChange={setActiveTab} />
                <ProductList products={products} onProductClick={handleProductClick} />
                <div className="flex justify-end gap-4 mt-8">
                    <Button
                        onClick={handleAddProduct}
                        className="inline-flex items-center justify-center gap-2 whitespace-nowrap text-sm font-medium transition-colors focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0 bg-ps-green hover:bg-ps-green/90 text-white px-4 py-2 shadow-lg rounded-full"
                    >
                        <Plus className="h-6 w-6" />
                        <span>{t('products.addProduct')}</span>
                    </Button>
                </div>
            </div>
        </Layout>
    );
};

export default Products;